import React, { useState, useEffect } from 'react';
import { FileText, Calendar, Package, Weight, Clock, Users, TrendingUp, Download } from 'lucide-react';
import { API_URL } from '../../services/api';

function RapportOrdresClotures() {
  const [ordres, setOrdres] = useState([]);
  const [totaux, setTotaux] = useState(null);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [loading, setLoading] = useState(false);
  const [ordreExpanded, setOrdreExpanded] = useState(null);
  const [statutFiltre, setStatutFiltre] = useState('tous');

  useEffect(() => {
    // Charger une période large par défaut pour voir tous les ordres
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), 0, 1); // 1er janvier de l'année en cours
    const lastDay = new Date(today.getFullYear(), 11, 31); // 31 décembre de l'année en cours
    
    setDateDebut(firstDay.toISOString().split('T')[0]);
    setDateFin(lastDay.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (dateDebut && dateFin) {
      loadRapport();
    }
  }, [dateDebut, dateFin, statutFiltre]);

  const loadRapport = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/atelier/rapport-ordres-clotures?date_debut=${dateDebut}&date_fin=${dateFin}&statut=${statutFiltre}`
      );
      if (response.ok) {
        const data = await response.json();
        setOrdres(data.ordres);
        setTotaux(data.totaux);
      }
    } catch (error) {
      console.error('Erreur chargement rapport:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const toggleOrdreDetail = (ordreId) => {
    setOrdreExpanded(ordreExpanded === ordreId ? null : ordreId);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText className="text-blue-600" />
          Rapport des Ordres de Fabrication
        </h1>
        <p className="text-gray-600 mt-1">Suivi détaillé de la production avec pourcentage d'avancement</p>
      </div>

      {/* Filtres de dates et statut */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="h-5 w-5 text-gray-600" />
          <span className="font-semibold">Filtres du rapport :</span>
        </div>
        
        {/* Filtres de statut */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setStatutFiltre('tous')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              statutFiltre === 'tous' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}>
            Tous les ordres
          </button>
          <button
            onClick={() => setStatutFiltre('en_cours')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              statutFiltre === 'en_cours' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}>
            En cours
          </button>
          <button
            onClick={() => setStatutFiltre('termine')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              statutFiltre === 'termine' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}>
            Terminés
          </button>
        </div>
        
        {/* Filtres de dates */}
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-1">Date de début</label>
            <input
              type="date"
              value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
              className="w-full border-2 border-gray-300 rounded px-3 py-2"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium mb-1">Date de fin</label>
            <input
              type="date"
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
              className="w-full border-2 border-gray-300 rounded px-3 py-2"
            />
          </div>
          <button
            onClick={loadRapport}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Générer le rapport
          </button>
        </div>
      </div>

      {/* Totaux généraux */}
      {totaux && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between mb-2">
              <Package className="h-8 w-8 opacity-80" />
              <div className="text-right">
                <div className="text-3xl font-bold">{totaux.nombre_ordres}</div>
                <div className="text-sm opacity-90">Ordres total</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="h-8 w-8 opacity-80" />
              <div className="text-right">
                <div className="text-3xl font-bold">{totaux.nombre_ordres_termines}</div>
                <div className="text-sm opacity-90">Terminés</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="h-8 w-8 opacity-80" />
              <div className="text-right">
                <div className="text-3xl font-bold">{totaux.nombre_ordres_en_cours}</div>
                <div className="text-sm opacity-90">En cours</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between mb-2">
              <Weight className="h-8 w-8 opacity-80" />
              <div className="text-right">
                <div className="text-3xl font-bold">{totaux.total_or_utilise}g</div>
                <div className="text-sm opacity-90">Or utilisé</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistiques détaillées */}
      {totaux && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Statistiques détaillées</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <div className="text-gray-600 text-sm">Bijoux produits</div>
              <div className="text-2xl font-bold text-green-600">{totaux.nombre_bijoux_total}</div>
            </div>
            <div>
              <div className="text-gray-600 text-sm">Or utilisé total</div>
              <div className="text-2xl font-bold text-yellow-600">{totaux.total_or_utilise}g</div>
            </div>
            <div>
              <div className="text-gray-600 text-sm">Durée moyenne production</div>
              <div className="text-2xl font-bold text-purple-600">{totaux.duree_moyenne_production} jours</div>
            </div>
          </div>
        </div>
      )}

      {/* Liste des ordres */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Chargement du rapport...</p>
        </div>
      ) : ordres.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 text-lg">Aucun ordre clôturé pour cette période</p>
        </div>
      ) : (
        <div className="space-y-4">
          {ordres.map((ordre) => (
            <div key={ordre.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* En-tête de l'ordre */}
              <div
                className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                onClick={() => toggleOrdreDetail(ordre.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-4">
                      <div className="text-2xl font-bold text-blue-600">{ordre.numero_ordre}</div>
                      <div>
                        <div className="font-semibold text-lg">{ordre.type_bijou}</div>
                        <div className="text-sm text-gray-600">{ordre.description}</div>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-6 gap-3 text-center">
                    <div>
                      <div className="text-xs text-gray-600">Complétion</div>
                      <div className={`text-2xl font-bold ${
                        ordre.pourcentage_completion === 100 ? 'text-green-600' :
                        ordre.pourcentage_completion >= 50 ? 'text-blue-600' :
                        'text-orange-600'
                      }`}>
                        {ordre.pourcentage_completion}%
                      </div>
                      <div className="text-xs text-gray-500">
                        {ordre.nombre_etapes_terminees}/{ordre.nombre_etapes_total} étapes
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Bijoux</div>
                      <div className="text-xl font-bold text-green-600">{ordre.nombre_bijoux}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Or</div>
                      <div className="text-xl font-bold text-yellow-600">{ordre.total_or_utilise || ordre.poids_or_reel || ordre.poids_metal || 0}g</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Pierres</div>
                      <div className="text-xl font-bold text-pink-600">{ordre.nombre_pierres || 0}</div>
                      {ordre.total_carats > 0 && (
                        <div className="text-xs text-gray-500">{ordre.total_carats}ct</div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Ouvriers</div>
                      <div className="text-xl font-bold text-purple-600">{ordre.nombre_ouvriers || 0}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Durée</div>
                      <div className={`text-xl font-bold ${
                        ordre.duree_production_jours > 30 ? 'text-red-600' :
                        ordre.duree_production_jours > 15 ? 'text-orange-600' :
                        'text-blue-600'
                      }`}>
                        {ordre.duree_production_jours} jours
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Détails de l'ordre (expandable) */}
              {ordreExpanded === ordre.id && (
                <div className="p-6 bg-gray-50">
                  <div className="grid grid-cols-2 gap-6 mb-6">
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-3">Informations générales</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Métal :</span>
                          <span className="font-semibold">{ordre.metal}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date commande :</span>
                          <span className="font-semibold">{formatDate(ordre.date_commande)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date clôture :</span>
                          <span className="font-semibold">{formatDate(ordre.date_fin_production)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Poids final :</span>
                          <span className="font-semibold">{ordre.poids_metal || 0}g</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-700 mb-3">Bilan Or</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Or donné :</span>
                          <span className="font-semibold text-blue-600">{ordre.total_or_donne || 0}g</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Or utilisé :</span>
                          <span className="font-semibold text-green-600">{ordre.total_or_utilise || 0}g</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Or restant :</span>
                          <span className="font-semibold text-yellow-600">{ordre.total_or_restant || 0}g</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Rendement :</span>
                          <span className="font-semibold text-purple-600">{ordre.rendement_or_pourcent}%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pierres et Diamants */}
                  {ordre.pierres_detail && ordre.pierres_detail.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        💎 Pierres et Diamants
                      </h3>
                      <div className="bg-white rounded-lg border-2 border-pink-200 p-4">
                        <div className="grid grid-cols-2 gap-4">
                          {ordre.pierres_detail.map((pierre, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-pink-50 rounded">
                              <div>
                                <div className="font-semibold text-sm">{pierre.designation}</div>
                                <div className="text-xs text-gray-600">{pierre.reference}</div>
                              </div>
                              <div className="text-right">
                                <div className="font-bold text-pink-600">{pierre.quantite_carats}ct</div>
                                {pierre.couleur && <div className="text-xs text-gray-500">{pierre.couleur}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-3 pt-3 border-t border-pink-200 flex justify-between">
                          <span className="font-semibold">Total carats :</span>
                          <span className="font-bold text-pink-600">{ordre.total_carats}ct</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Ouvriers impliqués */}
                  <div className="mb-6">
                    <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Ouvriers impliqués
                    </h3>
                    <div className="text-sm text-gray-600">{ordre.ouvriers_impliques || 'Aucun'}</div>
                  </div>

                  {/* Détail des étapes */}
                  {ordre.etapes_detail && ordre.etapes_detail.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-3">Détail des étapes</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-200">
                            <tr>
                              <th className="px-3 py-2 text-left">Étape</th>
                              <th className="px-3 py-2 text-left">Ouvrier</th>
                              <th className="px-3 py-2 text-left">Statut</th>
                              <th className="px-3 py-2 text-left">Début</th>
                              <th className="px-3 py-2 text-left">Fin</th>
                              <th className="px-3 py-2 text-left">Durée</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {ordre.etapes_detail.map((etape, idx) => (
                              <tr key={idx} className="hover:bg-gray-100">
                                <td className="px-3 py-2 font-medium">{etape.nom_etape}</td>
                                <td className="px-3 py-2">{etape.ouvrier || '-'}</td>
                                <td className="px-3 py-2">
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                    etape.statut === 'termine' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    {etape.statut === 'termine' ? 'Terminée' : 'En cours'}
                                  </span>
                                </td>
                                <td className="px-3 py-2">{formatDate(etape.date_debut)}</td>
                                <td className="px-3 py-2">{formatDate(etape.date_fin)}</td>
                                <td className="px-3 py-2">{etape.duree_reelle_heures || 0}h</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default RapportOrdresClotures;
